import { useState, useEffect, useRef } from 'react';
import { SchoolData, SecondaryContact } from '@/lib/onboarding-store';
import { WizardStepLayout } from './WizardStepLayout';
import { User, Mail, Phone, Briefcase, Plus, X, Users, CheckCircle, Loader2, ShieldCheck, AlertTriangle, Pencil } from 'lucide-react';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import { motion, AnimatePresence } from 'framer-motion';
import { sendEmailOtp, verifyEmailOtp } from '@/lib/api';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { isDemoMode, showDemoLock, showMailboxIntro } from '@/lib/demo/store';

// In demo mode the pre-seeded secondary contact (Jane Smith, SLT) is the
// only one that exists. Adding more would force the user to enter an
// email we'd then have to fake — instead we block the action with a
// popup so the rule is obvious.
function blockAddContactIfDemo(): boolean {
  if (!isDemoMode()) return false;
  showDemoLock({
    emoji: '🪪',
    title: 'Demo has one secondary contact',
    message:
      'The pre-seeded SLT contact is the only additional contact in the demo. Their invite email lands in the inbox bell so you can see the flow without exchanging real addresses.',
  });
  return true;
}

function generateId(): string {
  return 'sc_' + Math.random().toString(36).slice(2, 10);
}

interface SchoolStep2Props {
  data: Partial<SchoolData>;
  onChange: (field: keyof SchoolData, value: unknown) => void;
  onNext: () => void;
  onBack: () => void;
  onReturnToReview?: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parsePhoneLocal(value: string): string {
  return value.replace(/^\+\d+\s*/, '').replace(/\D/g, '');
}

function isPhoneValid(value: string): boolean {
  if (!value.trim()) return false;
  const local = parsePhoneLocal(value);
  if (local.length === 0) return false;
  const isUK = value.startsWith('+44');
  return isUK ? local.length === 10 : local.length >= 7;
}

function getErrors(data: Partial<SchoolData>, emailVerified: boolean): string[] {
  const errors: string[] = [];
  if (!data.contactFirstName?.trim()) errors.push('First name is required.');
  if (!data.contactLastName?.trim()) errors.push('Last name is required.');
  if (!data.contactTitle?.trim()) errors.push('Job title is required.');
  if (!data.contactEmail?.trim()) {
    errors.push('Email address is required.');
  } else if (!EMAIL_RE.test(data.contactEmail.trim())) {
    errors.push('Email address doesn\'t look valid.');
  } else if (!emailVerified) {
    errors.push('Please verify your email address before continuing.');
  }
  const contactPhoneLocal = parsePhoneLocal(data.contactPhone || '');
  if (!data.contactPhone?.trim() || contactPhoneLocal.length === 0) {
    errors.push('Phone number is required.');
  } else {
    const isUK = (data.contactPhone || '').startsWith('+44');
    if (isUK ? contactPhoneLocal.length !== 10 : contactPhoneLocal.length < 7) {
      errors.push(isUK ? 'Phone must be exactly 10 digits after +44.' : 'Phone number is invalid.');
    }
  }
  return errors;
}

const OTP_SESSION_KEY = 'cmc_otp_state';

function loadOtpSession(email: string): { sent: boolean; cooldownUntil: number; verified: boolean } {
  try {
    const raw = sessionStorage.getItem(OTP_SESSION_KEY);
    if (!raw) return { sent: false, cooldownUntil: 0, verified: false };
    const parsed = JSON.parse(raw);
    // Discard if it's for a different email
    if (parsed.email !== email) return { sent: false, cooldownUntil: 0, verified: false };
    return { sent: parsed.sent ?? false, cooldownUntil: parsed.cooldownUntil ?? 0, verified: parsed.verified ?? false };
  } catch {
    return { sent: false, cooldownUntil: 0, verified: false };
  }
}

function saveOtpSession(email: string, sent: boolean, cooldownUntil: number, verified: boolean) {
  try {
    sessionStorage.setItem(OTP_SESSION_KEY, JSON.stringify({ email, sent, cooldownUntil, verified }));
  } catch { /* ignore */ }
}

const PRESET_TITLES = ['Head Teacher','Deputy Head','Assistant Head','Office Manager','Business Manager','Cover Coordinator'];

export function SchoolStep2Contact({ data, onChange, onNext, onBack, onReturnToReview }: SchoolStep2Props) {
  const secondaryContacts: SecondaryContact[] = data.secondaryContacts || [];
  const [submitted, setSubmitted] = useState(false);
  // Track whether the user has explicitly selected "Other" for job title
  const [titleIsOther, setTitleIsOther] = useState(() =>
    !!data.contactTitle && !PRESET_TITLES.includes(data.contactTitle)
  );

  const email = (data.contactEmail || '').trim();

  // Restore OTP state from sessionStorage on mount
  const [otpSent, setOtpSent] = useState(() => loadOtpSession(email).sent);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(() => loadOtpSession(email).verified);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(() => {
    const until = loadOtpSession(email).cooldownUntil;
    const remaining = Math.ceil((until - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  });
  const prevEmailRef = useRef(email);

  // Reset OTP state when email changes
  useEffect(() => {
    const current = (data.contactEmail || '').trim();
    if (current !== prevEmailRef.current) {
      prevEmailRef.current = current;
      setOtpSent(false);
      setOtpVerified(false);
      setOtpCode('');
      setOtpError('');
      setOtpCooldown(0);
      saveOtpSession(current, false, 0, false);
    }
  }, [data.contactEmail]);

  // Countdown timer
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  const handleSendOtp = async () => {
    const currentEmail = (data.contactEmail || '').trim();
    if (!EMAIL_RE.test(currentEmail)) { setOtpError('Please enter a valid email first.'); return; }
    setOtpLoading(true);
    setOtpError('');
    try {
      await sendEmailOtp(currentEmail);
      const cooldownUntil = Date.now() + 60_000;
      setOtpSent(true);
      setOtpCooldown(60);
      saveOtpSession(currentEmail, true, cooldownUntil, false);
      // The demo sends no real email, so "check your inbox" is a dead end unless
      // the user knows the mailbox exists. This is the first moment they need
      // something OUT of it, so it's the one moment worth interrupting for; the
      // store makes sure it only ever happens once per demo run.
      if (isDemoMode()) showMailboxIntro();
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'Failed to send code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const email = (data.contactEmail || '').trim();
    if (otpCode.length < 6) { setOtpError('Please enter the 6-digit code.'); return; }
    setOtpLoading(true);
    setOtpError('');
    try {
      const result = await verifyEmailOtp(email, otpCode);
      if (!result.valid) {
        setOtpError(result.error ?? 'Invalid code. Please try again.');
        return;
      }
      setOtpVerified(true);
      setOtpError('');
      saveOtpSession((data.contactEmail || '').trim(), true, 0, true);
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const errors = getErrors(data, otpVerified);

  const [newContact, setNewContact] = useState<{ name: string; email: string; role: string; phone: string }>({
    name: '', email: '', role: '', phone: '',
  });
  // Tracks whether the dropdown is on "Other" — when true we render a free-text input
  // whose value is mirrored into newContact.role.
  const [roleIsOther, setRoleIsOther] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  // null = adding a brand-new contact; otherwise = editing the existing contact with this id.
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [secPhoneError, setSecPhoneError] = useState('');
  const [phoneDialogMsg, setPhoneDialogMsg] = useState('');

  const hasUnsavedContact = showAddForm && !!(
    newContact.name.trim() || newContact.email.trim() || newContact.role.trim() || newContact.phone.trim()
  );

  // Predefined contact roles for the dropdown. "Other" reveals a custom text input.
  const CONTACT_ROLE_OPTIONS = [
    'SLT',
    'Deputy Headteacher',
    'Assistant Headteacher',
    'Head of Year',
    'Head of Department',
    'Pastoral Lead',
    'Designated Safeguarding Lead',
    'Cover Coordinator',
    'School Business Manager',
    'Office Manager',
  ];

  const fieldError = (key: string) => submitted && errors.some(e => e.toLowerCase().startsWith(key));

  // A phone is "ready" only when local digits are present AND the full number passes validation.
  // This also handles the "+44 " (dial-code-only) edge case where .trim() is truthy but has 0 digits.
  const secPhoneDigits = parsePhoneLocal(newContact.phone);
  const secPhoneReady = secPhoneDigits.length > 0 && isPhoneValid(newContact.phone);

  const addSecondaryContact = () => {
    if (!newContact.name.trim() || !newContact.email.trim() || !newContact.role.trim()) return;
    // Hard block on invalid phone — covers every path including keyboard shortcuts
    if (!secPhoneReady) {
      const isUK = newContact.phone.startsWith('+44');
      const msg = secPhoneDigits.length === 0
        ? 'A phone number is required for additional admin contacts.'
        : isUK
          ? 'UK phone numbers must be exactly 10 digits after the +44 country code (e.g. 7123456789).'
          : 'Phone number is too short. Please enter a valid number.';
      setSecPhoneError(msg);
      setPhoneDialogMsg(msg);
      return;
    }
    setSecPhoneError('');
    const trimmedPhone = newContact.phone.trim() || undefined;
    // Editing an existing contact → splice the update into place. Adding → append.
    let updated;
    if (editingContactId) {
      updated = secondaryContacts.map(c =>
        c.id === editingContactId ? { ...c, ...newContact, phone: trimmedPhone } : c,
      );
    } else {
      updated = [...secondaryContacts, { id: generateId(), ...newContact, phone: trimmedPhone }];
    }
    onChange('secondaryContacts', updated);
    setNewContact({ name: '', email: '', role: '', phone: '' });
    setRoleIsOther(false);
    setShowAddForm(false);
    setEditingContactId(null);
  };

  const removeSecondaryContact = (id: string) => {
    onChange('secondaryContacts', secondaryContacts.filter(c => c.id !== id));
    // If the user removes the contact they're currently editing, also reset the form.
    if (editingContactId === id) {
      setNewContact({ name: '', email: '', role: '', phone: '' });
      setRoleIsOther(false);
      setShowAddForm(false);
      setEditingContactId(null);
    }
  };

  const editSecondaryContact = (contact: SecondaryContact) => {
    setEditingContactId(contact.id);
    setNewContact({
      name: contact.name,
      email: contact.email,
      role: contact.role,
      phone: contact.phone || '',
    });
    // If the contact's role isn't in our preset list, jump the dropdown to "Other" + show the text input
    setRoleIsOther(!CONTACT_ROLE_OPTIONS.includes(contact.role));
    setShowAddForm(true);
    setSecPhoneError('');
  };

  return (
    <WizardStepLayout
      currentStep={2}
      totalSteps={8}
      title="Main Contact"
      subtitle="Who should we contact about this account?"
      onNext={onNext}
      onBack={onBack}
      validationErrors={errors}
      onSubmitAttempt={() => setSubmitted(true)}
      onReturnToReview={onReturnToReview}
      unsavedDraftWarning={hasUnsavedContact
        ? (editingContactId
            ? 'You’re editing a contact but haven’t saved the changes yet.'
            : 'You’ve started adding a contact but haven’t saved it yet.')
        : undefined}
    >
      <div className="space-y-6">
        {/* Name row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-foreground">
              First Name <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                value={data.contactFirstName || ''}
                onChange={(e) => onChange('contactFirstName', e.target.value)}
                placeholder="John"
                className={`input-premium pl-12 ${fieldError('first') ? 'border-destructive' : ''}`}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-foreground">
              Last Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={data.contactLastName || ''}
              onChange={(e) => onChange('contactLastName', e.target.value)}
              placeholder="Smith"
              className={`input-premium ${fieldError('last') ? 'border-destructive' : ''}`}
            />
          </div>
        </div>

        {/* Job title */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-foreground">
            Job Title <span className="text-destructive">*</span>
          </label>
          {/* No leading icon on this one, unlike the name/email/phone fields.
              It is the only icon in the wizard that sat on a <select>, and iOS
              Safari does not honour horizontal padding on a NATIVE select — so
              `pl-12` was ignored there and the briefcase landed on top of the
              chosen job title. (Chrome honours it, which is why it measured
              clean on desktop and looked broken on a phone.)

              The alternative was appearance-none plus a hand-rolled chevron, but
              there is no appearance-none select anywhere else in this app, so
              that would make this the one non-native control app-wide and leave
              a custom chevron to keep matching three platforms. A labelled field
              with no icon is the smaller inconsistency. */}
          <div className="relative">
            <select
              value={titleIsOther ? 'Other' : (data.contactTitle || '')}
              onChange={(e) => {
                if (e.target.value === 'Other') {
                  setTitleIsOther(true);
                  onChange('contactTitle', '');
                } else {
                  setTitleIsOther(false);
                  onChange('contactTitle', e.target.value);
                }
              }}
              className={`input-premium ${fieldError('job') ? 'border-destructive' : ''}`}
            >
              <option value="">Select job title…</option>
              <option value="Head Teacher">Head Teacher</option>
              <option value="Deputy Head">Deputy Head</option>
              <option value="Assistant Head">Assistant Head</option>
              <option value="Office Manager">Office Manager</option>
              <option value="Business Manager">Business Manager</option>
              <option value="Cover Coordinator">Cover Coordinator</option>
              <option value="Other">Other…</option>
            </select>
          </div>
          {/* Free-text input when "Other" is chosen */}
          {titleIsOther && (
            <input
              type="text"
              value={data.contactTitle || ''}
              onChange={(e) => onChange('contactTitle', e.target.value)}
              placeholder="Enter your job title"
              autoFocus
              className={`input-premium ${fieldError('job') ? 'border-destructive' : ''}`}
            />
          )}
        </div>

        {/* Email + OTP verification */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-foreground">
            Email Address <span className="text-destructive">*</span>
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              {otpVerified && <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-success" />}
              <input
                type="email"
                value={data.contactEmail || ''}
                onChange={(e) => onChange('contactEmail', e.target.value)}
                placeholder="head@school.edu"
                disabled={otpVerified}
                className={`input-premium pl-12 ${otpVerified ? 'pr-10 border-success/50 bg-success/5' : ''} ${fieldError('email') ? 'border-destructive' : ''}`}
              />
            </div>
            {!otpVerified && (
              <button
                type="button"
                onClick={otpSent ? handleSendOtp : handleSendOtp}
                disabled={otpLoading || otpCooldown > 0 || !EMAIL_RE.test((data.contactEmail || '').trim())}
                className="flex-shrink-0 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {otpCooldown > 0 ? `Resend (${otpCooldown}s)` : otpSent ? 'Resend' : 'Verify'}
              </button>
            )}
          </div>

          {/* OTP input — shown once code is sent */}
          <AnimatePresence>
            {otpSent && !otpVerified && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="p-3 bg-muted/40 rounded-xl border border-border space-y-2 mt-1">
                  <p className="text-xs text-muted-foreground">
                    We sent a 6-digit code to <strong className="text-foreground">{data.contactEmail}</strong>. Enter it below to verify.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="input-premium text-center text-lg font-mono tracking-widest w-36"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={otpLoading || otpCode.length < 6}
                      className="flex-1 px-4 py-2 rounded-xl bg-success text-success-foreground text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Confirm
                    </button>
                  </div>
                  {otpError && <p className="text-xs text-destructive">{otpError}</p>}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {otpVerified ? (
            <div className="flex items-center justify-between">
              <p className="text-xs text-success flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" /> Email verified successfully
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              This will be your admin login email, we'll send a verification code to confirm it.
            </p>
          )}
          {otpError && !otpSent && <p className="text-xs text-destructive">{otpError}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-foreground">
            Phone Number <span className="text-destructive">*</span>
          </label>
          <PhoneInput
            value={data.contactPhone || ''}
            onChange={(v) => onChange('contactPhone', v)}
            placeholder="07123456789"
            required
            leftSlot={<Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />}
          />
        </div>

        {/* Info box */}
        <div className="p-4 bg-muted/50 rounded-xl border border-border">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Note:</strong> This person will be the primary administrator 
            and can invite other staff members to the platform.
          </p>
        </div>

        {/* =================== SECONDARY CONTACTS =================== */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-foreground">Additional Admin Contacts</h3>
            <span className="text-xs text-muted-foreground">(optional)</span>
            <HelpTooltip text="Additional admin users with full access to the platform. Each secondary contact will receive an email invitation with an activation link when you complete setup." />
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Add delegates such as SLT members, Heads of Year, or other key staff who should have contact visibility.
          </p>

          {/* List of added secondary contacts */}
          <AnimatePresence>
            {secondaryContacts.map((contact) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3"
              >
                <div className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${editingContactId === contact.id ? 'bg-primary/5 border-primary/40' : 'bg-muted/30 border-border'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-sm text-foreground">{contact.name}</div>
                      <div className="text-xs text-muted-foreground">{contact.role} • {contact.email}{contact.phone ? ` • ${contact.phone}` : ''}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => editSecondaryContact(contact)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                      title="Edit contact"
                      aria-label={`Edit ${contact.name}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => removeSecondaryContact(contact.id)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      title="Remove contact"
                      aria-label={`Remove ${contact.name}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Add contact form */}
          <AnimatePresence>
            {showAddForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden mb-3"
              >
                <div className="p-4 bg-muted/20 rounded-xl border border-border space-y-3">
                  {/* Header reflects whether we're editing an existing contact or adding a new one */}
                  <div className="flex items-center gap-2 pb-2 border-b border-border/60">
                    {editingContactId
                      ? <Pencil className="w-4 h-4 text-primary" />
                      : <Plus className="w-4 h-4 text-primary" />}
                    <span className="text-sm font-semibold text-foreground">
                      {editingContactId ? 'Edit contact' : 'Add another contact'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-foreground">
                        Full name <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        value={newContact.name}
                        onChange={(e) => setNewContact(c => ({ ...c, name: e.target.value }))}
                        placeholder="e.g. Jane Smith"
                        className="input-premium text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-foreground">
                        Role <span className="text-destructive">*</span>
                      </label>
                      <select
                        value={roleIsOther ? '__other__' : (CONTACT_ROLE_OPTIONS.includes(newContact.role) ? newContact.role : '')}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '__other__') {
                            setRoleIsOther(true);
                            setNewContact(c => ({ ...c, role: '' }));
                          } else {
                            setRoleIsOther(false);
                            setNewContact(c => ({ ...c, role: v }));
                          }
                        }}
                        className="input-premium text-sm"
                      >
                        <option value="">Select a role…</option>
                        {CONTACT_ROLE_OPTIONS.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                        <option value="__other__">Other (type your own)</option>
                      </select>
                      {roleIsOther && (
                        <input
                          type="text"
                          value={newContact.role}
                          onChange={(e) => setNewContact(c => ({ ...c, role: e.target.value }))}
                          placeholder="Type the role (e.g. Curriculum Lead)"
                          className="input-premium text-sm mt-2"
                          autoFocus
                        />
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-foreground">
                        Email address <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact(c => ({ ...c, email: e.target.value }))}
                        placeholder="jane@school.edu"
                        className="input-premium text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-foreground">
                        Phone number <span className="text-destructive">*</span>
                      </label>
                      <PhoneInput
                        value={newContact.phone}
                        onChange={(v) => { setNewContact(c => ({ ...c, phone: v })); setSecPhoneError(''); }}
                        placeholder="Phone number"
                        className="text-sm"
                      />
                      {secPhoneError && <p className="text-xs text-destructive mt-1">{secPhoneError}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={addSecondaryContact}
                      disabled={!newContact.name.trim() || !newContact.email.trim() || !newContact.role.trim() || !secPhoneReady}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editingContactId ? 'Save Changes' : 'Add Contact'}
                    </button>
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setNewContact({ name: '', email: '', role: '', phone: '' });
                        setRoleIsOther(false);
                        setSecPhoneError('');
                        setEditingContactId(null);
                      }}
                      className="px-4 py-2 rounded-lg bg-muted text-foreground text-sm font-medium hover:bg-muted/80 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {!showAddForm && (
            <button
              onClick={() => {
                if (blockAddContactIfDemo()) return;
                setShowAddForm(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-border text-sm font-medium text-muted-foreground hover:border-primary/30 hover:text-primary transition-colors w-full justify-center"
            >
              <Plus className="w-4 h-4" />
              Add Admin Contact
            </button>
          )}
        </div>
      </div>

      {/* Phone number validation dialog */}
      <AlertDialog open={!!phoneDialogMsg} onOpenChange={(open) => !open && setPhoneDialogMsg('')}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Invalid Phone Number
            </AlertDialogTitle>
            <AlertDialogDescription>{phoneDialogMsg}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setPhoneDialogMsg('')}>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WizardStepLayout>
  );
}
